import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Form,
  FormInstance,
  Row,
  Col,
  Skeleton,
  Input,
  Checkbox,
  Button,
  Modal,
  Tag,
  Divider,
  Typography,
} from 'antd';

import {
  useDefaultEmptyFieldError,
  useDefaultMinLengthFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
  useDebounce,
} from '@/hooks';

import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';

import { useCreateNcm } from '@/core/domain/operational/Ncm/Ncm.hooks';

import {
  FloatingFormItem,
  ImagePickerFormField,
  UploadFile,
  InputDecimal,
  InfiniteTable,
} from '@/components/_common';
import NcmOutgoingForm from '@/components/Fiscal/Ncm/NcmForm/NcmOutgoingForm';
import { useForm } from 'antd/es/form/Form';
import NcmIncomingForm from '@/components/Fiscal/Ncm/NcmForm/NcmIncomingForm';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import CfopSelectField from '@/components/_common/CfopSelectField';
import NcmSelectField from '@/components/_common/NcmSelectField';
import { GroupSelect } from '@/core/domain/operational/Groups/Groups.types';
import { Part } from '@/core/domain/operational/Parts/Parts.types';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import Authorities from '@/types/Authority';
import {
  useValidateBarCode,
  useValidateManufacturingCode,
} from '@/core/domain/operational/Parts/Parts.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { applyCpfCnpjMask } from '@/utils/mask';
import { useCompaniesReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { TaxeIcmsOriginSelect } from '@/components/_common/TaxesSelect';
import CreateGroupWithSubGroupModal from '../../Groups/CreateGroupWithSubGroupModal';

export interface PartFormData {
  active: boolean;
  anpCode?: string;
  anpDescription?: string;
  barCode?: string;
  cfopId: number;
  comment?: string;
  imageUrl: UploadFile<UploadArchiveResult>[];
  includeInDiagnosis: boolean;
  kmToMaintenance: number;
  lifespan: number;
  localizaCode: string;
  manufacturerId: number;
  manufacturingCode?: string;
  companyCode?: string;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
  catalogProductId?: string;
}

interface PartFormProps {
  form: FormInstance<PartFormData>;
  isLoading: boolean;
  partDetail?: Part;
  isModal?: boolean;
  disabledFields?: Partial<Record<keyof PartFormData, boolean | undefined>>;
  withInitialStock?: boolean;
}

function PartForm({
  form,
  isLoading,
  partDetail,
  isModal = false,
  disabledFields,
  withInitialStock = false,
}: PartFormProps) {
  const intl = useIntl();
  const [ncmForm] = useForm();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const companyList = useCompaniesReduced(
    {
      isCompanyFilter: true,
      includeBlocked: false,
      active: true,
    },
    withInitialStock,
  );
  const companies = flatInfiniteData(companyList.data);

  const [ncmModal, setNcmModal] = useDisclosure();

  const [groupModalOpened, groupModalActions] = useDisclosure();

  const createNcm = useCreateNcm();

  const [selectedGroup, setSelectedGroup] = useState<GroupSelect[]>([]);

  const validateManufacturingCode = useValidateManufacturingCode();
  const validateBarCode = useValidateBarCode();

  const manufacturingCode = Form.useWatch('manufacturingCode', form);
  const manufacturingCodeDebounce = useDebounce(manufacturingCode, 500);

  const barCode = Form.useWatch('barCode', form);
  const barCodeDebounce = useDebounce(barCode, 500);

  useEffect(() => {
    if (manufacturingCodeDebounce) {
      validateManufacturingCode.mutateAsync({
        manufacturingCode: manufacturingCodeDebounce,
        partId: partDetail?.id,
      });
    }
  }, [manufacturingCodeDebounce]);

  useEffect(() => {
    if (
      barCodeDebounce &&
      barCodeDebounce.toUpperCase() !== 'SEM GTIN' &&
      barCodeDebounce.length >= 8
    ) {
      validateBarCode.mutateAsync({
        barCode: barCodeDebounce,
        partId: partDetail?.id,
      });
    } else {
      validateBarCode.reset();
    }
  }, [barCodeDebounce]);

  useEffect(() => {
    if (partDetail?.subGroup) {
      setSelectedGroup([
        {
          id: partDetail.subGroup?.id,
          name: partDetail.subGroup?.name,
          subGroups: [partDetail.subGroup],
        },
      ]);
    }
  }, [partDetail]);

  const handleNewNcm = async () => {
    const values = await ncmForm.validateFields();
    try {
      createNcm
        .mutateAsync({ ...values, active: true })
        .then((response) => {
          form.setFieldValue('ncmId', response.id);
        })
        .then(() => ncmForm.resetFields());
      setNcmModal.close();
    } catch {}
  };

  return (
    <>
      <Form
        form={form}
        name={isModal ? undefined : 'generalParts'}
        preserve={false}
      >
        <Form.Item hidden name="catalogProductId" />

        <Skeleton active loading={isLoading}>
          <Row gutter={[16, 9]} id="part-create-form-data">
            <Col xs={24} sm={24} md={12}>
              <FloatingFormItem
                name="name"
                label={intl.formatMessage({ id: 'parts.entity.name' })}
                rules={[defaultEmptyFieldError]}
              >
                <Input disabled={disabledFields?.name} maxLength={200} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="manufacturerId"
                label={intl.formatMessage({ id: 'parts.entity.manufacturer' })}
                rules={[defaultEmptyFieldError]}
              >
                <ManufacturerInfiniteSelectField
                  queryFilter={{ active: true }}
                  disabled={disabledFields?.manufacturerId}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="manufacturingCode"
                label={intl.formatMessage({
                  id: 'parts.entity.manufacturerCode',
                })}
              >
                <Input
                  disabled={disabledFields?.manufacturingCode}
                  maxLength={50}
                />
              </FloatingFormItem>

              {validateManufacturingCode.data && !!manufacturingCode && (
                <Tag
                  color="yellow"
                  style={{ width: '100%', whiteSpace: 'normal' }}
                >
                  Este código de fabricante já está em uso.
                </Tag>
              )}
            </Col>

            <Col xs={24} sm={24} md={24} lg={12}>
              <FloatingFormItem
                name="cfopId"
                label={intl.formatMessage({ id: 'parts.entity.cfop' })}
              >
                <CfopSelectField disabled={disabledFields?.cfopId} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <FloatingFormItem
                name="ncmId"
                label={intl.formatMessage({ id: 'parts.entity.ncm' })}
              >
                <NcmSelectField
                  queryFilter={{ active: true }}
                  disabled={disabledFields?.ncmId}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={4}>
              <Button
                type="primary"
                size="large"
                onClick={setNcmModal.open}
                block
                disabled={
                  !hasPermissions([
                    Authorities.REGISTER_NCM_ADD,
                    Authorities.REGISTER_NCM_REGISTRATION,
                  ]) || disabledFields?.ncmId
                }
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                {intl.formatMessage({ id: 'ncm.create.new' })}
              </Button>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="subGroupId"
                label={intl.formatMessage({ id: 'parts.entity.subGroup' })}
                rules={[defaultEmptyFieldError]}
              >
                <GroupsInfiniteSelectField
                  searchBySubGroup
                  selectedGroup={selectedGroup}
                  disabled={disabledFields?.subGroupId}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={4}>
              <Button
                type="primary"
                size="large"
                onClick={groupModalActions.open}
                block
                disabled={
                  !hasPermissions([
                    Authorities.REGISTER_GROUP_ADD,
                    Authorities.REGISTER_GROUP_REGISTRATION,
                  ]) || disabledFields?.subGroupId
                }
                style={{ paddingLeft: 0, paddingRight: 0 }}
                id="part-create-form-group"
              >
                Novo Grupo
              </Button>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="barCode"
                label={intl.formatMessage({ id: 'parts.entity.barCode' })}
                rules={[
                  defaultMinLenghtFieldError(8),
                  defaultMaxLenghtFieldError(14),
                  defaultEmptyFieldError,
                ]}
              >
                <Input disabled={disabledFields?.barCode} maxLength={50} />
              </FloatingFormItem>

              {!!validateBarCode.data && !!barCode && (
                <Tag
                  color="yellow"
                  style={{ width: '100%', whiteSpace: 'normal' }}
                >
                  Este código de barras já está em uso.
                </Tag>
              )}
            </Col>

            <Col xs={24} sm={24} md={4}>
              <FloatingFormItem
                name="sku"
                label={intl.formatMessage({
                  id: 'parts.entity.sku',
                })}
              >
                <Input disabled maxLength={50} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={4}>
              <FloatingFormItem
                name="companyCode"
                label={intl.formatMessage({
                  id: 'parts.entity.companyCode',
                })}
              >
                <Input disabled={disabledFields?.companyCode} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={4}>
              <FloatingFormItem
                name="unit"
                label={intl.formatMessage({ id: 'parts.entity.unit' })}
                rules={[defaultEmptyFieldError, { max: 5 }]}
              >
                <Input maxLength={5} disabled={disabledFields?.unit} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="kmToMaintenance"
                label={intl.formatMessage({
                  id: 'parts.entity.kmToMaintenance',
                })}
              >
                <InputDecimal
                  fractions={0}
                  disabled={disabledFields?.kmToMaintenance}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="lifespan"
                label={intl.formatMessage({ id: 'parts.entity.lifeSpan' })}
              >
                <InputDecimal
                  fractions={0}
                  disabled={disabledFields?.lifespan}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="netWeight"
                label={intl.formatMessage({ id: 'parts.entity.netWeight' })}
              >
                <InputDecimal disabled={disabledFields?.kmToMaintenance} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <FloatingFormItem
                name="grossWeight"
                label={intl.formatMessage({ id: 'parts.entity.grossWeight' })}
              >
                <InputDecimal disabled={disabledFields?.kmToMaintenance} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <FloatingFormItem name="icmsOrigin" label="Origem">
                <TaxeIcmsOriginSelect />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Dados ANP / Combustíveis
                </Typography.Title>
                <span>
                  Obrigatório apenas para combustíveis, lubrificantes e
                  derivados.
                </span>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={6}>
                  <FloatingFormItem
                    name="anpCode"
                    label="Código ANP"
                    rules={[defaultMaxLenghtFieldError(9)]}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>

                <Col xs={24} sm={24} md={6}>
                  <FloatingFormItem name="anpDescription" label="Descrição ANP">
                    <Input />
                  </FloatingFormItem>
                </Col>
              </Row>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                noStyle
                name="includeInDiagnosis"
                valuePropName="checked"
              >
                <Checkbox
                  style={{ marginTop: '12px' }}
                  disabled={disabledFields?.includeInDiagnosis}
                >
                  <FormattedMessage id="parts.entity.includeInDiagnosis" />
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <FloatingFormItem
                name="comment"
                label={intl.formatMessage({ id: 'parts.entity.comment' })}
              >
                <Input.TextArea
                  rows={3}
                  style={{ height: '104px' }}
                  disabled={disabledFields?.comment}
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <ImagePickerFormField
                maxFileCount={1}
                fieldName="imageUrl"
                disabled={disabledFields?.imageUrl}
              />
            </Col>
          </Row>

          {withInitialStock && (
            <div id="part-form-company-stock">
              <Divider />

              <Typography.Title level={4}>Estoque inicial</Typography.Title>

              <InfiniteTable
                dataSource={companies}
                isFetching={companyList.isFetching}
                loading={companyList.isLoading}
                hasNextPage={companyList.hasNextPage}
                fetchNextPage={companyList.fetchNextPage}
                columns={[
                  {
                    title: 'Empresa',
                    render: (_, item, index) => (
                      <>
                        <Form.Item
                          hidden
                          name={['partStockSet', index, 'companyId']}
                          initialValue={item.id}
                        />
                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                        <div>
                          {item.cnpj ? applyCpfCnpjMask(item.cnpj) : '-'}
                        </div>
                      </>
                    ),
                  },
                  {
                    title: 'Quantidade',
                    width: 200,
                    render: (_, __, index) => (
                      <FloatingFormItem
                        label="Quantidade"
                        noStyle
                        style={{ margin: 0 }}
                        name={['partStockSet', index, 'quantity']}
                        initialValue={0}
                      >
                        <InputDecimal min={0} />
                      </FloatingFormItem>
                    ),
                  },
                ]}
                rowKey={(row) => row.id}
              />
            </div>
          )}
        </Skeleton>

        <Modal
          destroyOnClose
          open={ncmModal}
          onOk={handleNewNcm}
          onCancel={() => {
            setNcmModal.close();
            ncmForm.resetFields();
          }}
          title={intl.formatMessage({ id: 'ncm.create.new' })}
        >
          <NcmOutgoingForm form={ncmForm} isModal />
          <NcmIncomingForm form={ncmForm} isModal />
        </Modal>
      </Form>

      <CreateGroupWithSubGroupModal
        onCallback={(subGroupId) =>
          form.setFieldValue('subGroupId', subGroupId)
        }
        onCancel={groupModalActions.close}
        open={groupModalOpened}
      />
    </>
  );
}

export default PartForm;
